import { ElementType, FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalProps } from '@app/context/ModalsContext'

import { Body1, Body2, Caption2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import { MultiLevelCommissionDetailDto } from '@model/MultiLevelCommission'
import {
  CalculationMethod,
  PayoutSchemeAssetClassType,
  TradingAccountType,
} from '@model/MultiTierDto'
import { AccountTypes, PayoutSchemeDto } from '@model/PayoutScheme'

import { Box } from '@shared/components/Box'

import { iconByLevel } from '../constants/multiLevelCommission'

import styles from './PayoutScheme.module.scss'

export interface LevelsProps {
  activePayoutScheme?: PayoutSchemeDto
  onInfoModalOpen: (overrides?: Partial<Omit<BaseModalProps, 'id'>> | undefined) => string
}

interface LevelProps {
  kpi: MultiLevelCommissionDetailDto
  payoutScheme: AccountTypes
  activePayoutSchemeId?: number
  onInfoModalOpen: (overrides?: Partial<Omit<BaseModalProps, 'id'>> | undefined) => string
}

const PerSpreadShare = ({
  icon: Icon,
  spreadShare,
}: {
  icon: ElementType | null
  spreadShare: number
}) => {
  const { t } = useTranslation()

  return (
    <>
      <Box flex align='center' gap='x16'>
        {Icon && (
          <div>
            <Icon />
          </div>
        )}
        <Body2 color='text-med-em'>{t('IB.Dashboard.Current level')}</Body2>
      </Box>

      <Box flex gap='x16'>
        <Badge type='yellow' size='xs' contrast='high'>
          <Caption2>{spreadShare}%</Caption2>
        </Badge>
        <Body2 color='text-med-em'>{t('IB.Spread Share')}</Body2>
      </Box>
    </>
  )
}

const PerPercentBaseCurrency = ({ amount }: { amount: number }) => {
  const { t } = useTranslation()

  return (
    <>
      <Badge type='yellow' size='xs' contrast='high'>
        <Caption2>
          {/* Divided by 2 because amount includes open & closed trade */}
          {`${Number.isFinite(amount) ? amount / 2 : '-'}%`}
        </Caption2>
      </Badge>

      <Body2 color='text-med-em'>{t('IB.Dashboard.Your Payout Scheme.of Notional Volume')}</Body2>
    </>
  )
}

const CLASSIC_ASSET_CLASS_ID = 1

const CALC_METHOD_GAPS = {
  PERCENT_BASE: 'x16',
  MILLION_USD_OR_POINT_BASED: 'x0',
  DEFAULT: 'x8',
} as const

const PayoutSchemeAccountType = (props: LevelProps) => {
  const isMobile = useWindowResize()
  const { callGAEvents } = useGAEvents()
  const { t } = useTranslation()

  const { kpi, payoutScheme, activePayoutSchemeId, onInfoModalOpen } = props

  const Icon = iconByLevel[kpi?.levels?.current?.id] ?? null

  const isClassicAccountType = payoutScheme.accountType?.id === TradingAccountType.Classic
  const isVipAccountType = payoutScheme.accountType?.id === TradingAccountType.VIP
  const isProAccountType = payoutScheme.accountType?.id === TradingAccountType.Pro
  const hasInstrumentType = payoutScheme.instruments !== null

  const calculationMethod = {
    isPerPercentBaseCurrency:
      payoutScheme.calculationMethod.id === CalculationMethod.PER_PERCENT_BASE_CURRENCY,
    isPerLot: payoutScheme.calculationMethod.id === CalculationMethod.PER_LOT,
    isPerMillionUsd: payoutScheme.calculationMethod.id === CalculationMethod.PER_MILLION_USD,
    isPointBased: payoutScheme.calculationMethod.id === CalculationMethod.POINT_BASED,
  }

  const isClassicTabActive = activePayoutSchemeId === CLASSIC_ASSET_CLASS_ID
  const title = isClassicAccountType
    ? t('IB.MultiTier.IB Summary Classic')
    : payoutScheme.accountType?.name
    ? t(`IB.MultiTier.${payoutScheme.accountType.name}`)
    : null

  const nonKpiCalcMethodPrefix = calculationMethod.isPerLot ? `${getCurrencySymbol('USD')} ` : ''
  const nonKpiCalcMethodValue =
    !calculationMethod.isPerMillionUsd && !calculationMethod.isPointBased ? payoutScheme.amount : ''
  const nonKpiCalcMethodAmount = `${nonKpiCalcMethodPrefix}${nonKpiCalcMethodValue}`

  const nonKpiCalcMethodName = payoutScheme.calculationMethod.name
    ? t(`IB.Dashboard.Your Payout Scheme.${payoutScheme.calculationMethod.name}`, {
        defaultValue: payoutScheme.calculationMethod?.name ?? '',
      })
    : ''

  const nonKpiCalcMethodGapSize = useMemo(() => {
    if (!isClassicTabActive && calculationMethod.isPerPercentBaseCurrency) {
      return CALC_METHOD_GAPS.PERCENT_BASE
    }

    if (calculationMethod.isPerMillionUsd || calculationMethod.isPointBased) {
      return CALC_METHOD_GAPS.MILLION_USD_OR_POINT_BASED
    }

    return CALC_METHOD_GAPS.DEFAULT
  }, [
    isClassicTabActive,
    calculationMethod.isPerPercentBaseCurrency,
    calculationMethod.isPerMillionUsd,
    calculationMethod.isPointBased,
  ])

  // WCA-6832 - might be temporary payout changes
  const hideProAndVip = isProAccountType || isVipAccountType
  const hideEmptyClassicAccountType = payoutScheme.amount <= 0 && !isClassicAccountType
  const hideFilledInstrumentType = hasInstrumentType
  const hideMAMInvestor = payoutScheme.accountType?.id === TradingAccountType.MAM_Investor

  if (hideEmptyClassicAccountType || hideProAndVip || hideMAMInvestor || hideFilledInstrumentType) {
    return null
  }

  return (
    <div className={styles.accountTypeContainer}>
      {title && (
        <Box flex align='center' gap='x4'>
          <Body1 semibold color='text-high-em'>
            {title}
          </Body1>

          {isClassicAccountType && (
            <Tooltip disabled={isMobile} content={t('More info')}>
              <ActionIcon
                icon={InfoIcon}
                size='x16'
                onClick={() => {
                  callGAEvents().IBDashboard.MultiLevelCommission.moreInfo()
                  onInfoModalOpen()
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {/* kpi exists only for spread share calculation method (Classic) */}
      {kpi && isClassicAccountType ? (
        <PerSpreadShare icon={Icon} spreadShare={kpi.levels.current.spreadShare} />
      ) : (
        <Box flex gap={nonKpiCalcMethodGapSize}>
          {calculationMethod.isPerPercentBaseCurrency ? (
            <PerPercentBaseCurrency amount={payoutScheme.amount} />
          ) : (
            <>
              <Body2 semibold>{nonKpiCalcMethodAmount}</Body2>
              <Body2 color='text-med-em'>{nonKpiCalcMethodName}</Body2>
            </>
          )}
        </Box>
      )}
    </div>
  )
}

export const PayoutSchemeAssetClass: FC<LevelsProps> = (props) => {
  const { activePayoutScheme, onInfoModalOpen } = props
  const { t } = useTranslation()

  const activePayoutSchemeId = activePayoutScheme?.assetClass.id

  const isCryptoOrOtherScheme =
    activePayoutScheme?.assetClass.id === PayoutSchemeAssetClassType.Crypto ||
    activePayoutScheme?.assetClass.id === PayoutSchemeAssetClassType.Other
  const hasClassicAccountType = activePayoutScheme?.accountTypes?.some(
    ({ accountType }) => accountType?.id === TradingAccountType.Classic
  )

  return (
    <Box p='x8' mt='x16'>
      {isCryptoOrOtherScheme && !hasClassicAccountType && (
        <Box mb='x12'>
          <Body1 color='text-high-em' semibold>
            {`${t('IB.MultiTier.IB Summary Classic')} & ${t('IB.MultiTier.Raw')}`}
          </Body1>
        </Box>
      )}

      <div className={styles.accountTypesContainer}>
        {activePayoutScheme?.accountTypes?.map((payoutScheme, index) => {
          return (
            <PayoutSchemeAccountType
              key={payoutScheme.accountType?.id ?? index}
              payoutScheme={payoutScheme}
              kpi={activePayoutScheme.kpi}
              activePayoutSchemeId={activePayoutSchemeId}
              onInfoModalOpen={onInfoModalOpen}
            />
          )
        }) || []}
      </div>
    </Box>
  )
}
